// File: frontend/src/components/ui/KpiCard.js
//
// THE KPI CARD — the product's one headline-figure language, built on the
// Router Dashboard's ACTUAL card surface.
//
// The first convergence pass copied the Dashboard's rules (no sparkline, LIVE
// as a word, neutral figures) onto a flat paper tile, and the result read as a
// cheaper cousin of the reference: thinner, flatter, tighter. This version takes
// the reference's surface itself — the same glass background, blur, hairline
// border, inset highlight, 16px radius and faint accent wash, read from the SAME
// token skin (components/dashboard/dashboardTokens.js) the Dashboard band uses —
// and the same internal hierarchy: an uppercase label row with a tinted icon
// chip on the right, a 25px tabular figure, an 11px caption. So a card on
// Business Overview and a card on Router Dashboard are the same object.
//
// WHAT IT KEEPS FROM THE BUSINESS SIDE
//   · LIVE IS A WORD. A figure that ignores the date range carries the literal
//     word LIVE (the Dashboard's own pill), not a colour.
//   · LOADING IS NOT ZERO. A skeleton bar, never a confident "UGX 0".
//   · The change indicator (week-over-week) is the only semantic colour.
//   · Titles are never ellipsised: "AVAILABLE TO WITHDRAW" wraps rather than
//     becoming a number with no subject.
//   · NO SPARKLINE. A 26px line with no axis cannot be read against anything.
//
//   <KpiCard label="Available to Withdraw" value="UGX 2,283,020" icon={<WalletIcon />}
//            accent={theme.palette.success.main} live support="Ready to send" />
//
// Older call sites keep working: `icon` may be a component or an element,
// `color` is accepted for `accent`, `sub`/`caption` for `support`, and
// `delta` (+ `deltaGoodWhenUp`, `deltaSuffix`) for `change`.

import React, { useMemo } from "react";
import { Box, Typography, Tooltip, alpha, useTheme } from "@mui/material";
import {
  InfoOutlined as InfoIcon,
  ArrowDownward as DownIcon,
  ArrowUpward as UpIcon,
} from "@mui/icons-material";
import makeTokens from "../dashboard/dashboardTokens";
import LiveTag from "./LiveTag";

/**
 * @param {string}  label      the figure's name
 * @param {node}    value      preformatted headline
 * @param {node|elementType} [icon]  recognition mark (element, or a component)
 * @param {string}  [accent]   colour for the icon chip only (alias: `color`)
 * @param {node}    [support]  the line under the figure (aliases: `sub`, `caption`)
 * @param {string}  [hint]     one sentence, on a focusable info mark
 * @param {boolean} [live]     the figure ignores the date range
 * @param {{pct:number, better:boolean, up?:boolean, note?:string, text?:string}} [change]
 * @param {boolean} [loading]
 * @param {Function} [onClick] makes the whole card a button
 */
export default function KpiCard({
  label,
  value,
  icon,
  accent,
  color,
  support,
  sub,
  caption,
  hint,
  live = false,
  change,
  delta,
  deltaSuffix = "%",
  deltaGoodWhenUp = true,
  loading = false,
  onClick,
  sx,
}) {
  const theme = useTheme();
  // The Router Dashboard's own skin, so the surface cannot drift from it.
  const G = useMemo(() => makeTokens(theme.palette.mode), [theme.palette.mode]);
  const accentColour = (typeof accent === "string" && accent) || color || theme.palette.primary.main;
  const supportText = support ?? sub ?? caption;

  // `delta` is the older numeric form. Direction and meaning are separate
  // facts: a fall in failures is DOWN and BETTER.
  const resolvedChange = change || (typeof delta === "number" && Number.isFinite(delta)
    ? {
        pct: delta,
        up: delta >= 0,
        better: (delta >= 0) === deltaGoodWhenUp,
        text: `${Math.abs(delta)}${deltaSuffix}`,
      }
    : null);
  const changeUp = resolvedChange ? (resolvedChange.up ?? resolvedChange.better) : false;
  const changeText = resolvedChange
    ? (resolvedChange.text || `${Math.abs(Number(resolvedChange.pct) || 0).toFixed(1)}%`)
    : null;

  const iconNode = React.isValidElement(icon)
    ? icon
    : icon ? React.createElement(icon, { sx: { fontSize: 16 } }) : null;

  const interactive = typeof onClick === "function";
  const interactiveProps = interactive ? {
    role: "button",
    tabIndex: 0,
    onClick,
    onKeyDown: (e) => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(e); }
    },
  } : {};

  return (
    <Box
      {...interactiveProps}
      sx={{
        // ── The Dashboard band's surface (glassSx + heroGradient) ──
        position: "relative", overflow: "hidden",
        bgcolor: G.glassBg,
        backgroundImage: G.heroGradient,
        backdropFilter: G.glassBlur,
        WebkitBackdropFilter: G.glassBlur,
        border: `1px solid ${G.glassBorder}`,
        borderRadius: "16px",
        boxShadow: `${G.shadow}, ${G.glassInset}`,
        p: { xs: 1.6, md: 2 },
        display: "flex", flexDirection: "column",
        height: "100%",
        // The Dashboard card's natural height (label row, figure, caption and
        // the baseline strip under it). A band of these must not collapse into
        // thin slats when a caption is short.
        minHeight: { xs: 128, md: 148 },
        minWidth: 0,
        cursor: interactive ? "pointer" : "default",
        transition: "border-color .18s, transform .18s, box-shadow .18s",
        ...(interactive ? {
          "&:hover": {
            borderColor: G.borderHov, transform: "translateY(-2px)",
            boxShadow: `${G.shadowUp}, ${G.glassInset}`,
          },
          "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
        } : null),
        "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
        ...sx,
      }}
    >
      {/* Top row: label + time-basis marker on the left, icon chip on the right. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 1, mb: { xs: 0.9, md: 1.25 } }}>
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 0.5, minWidth: 0, pt: "3px" }}>
          <Typography
            component="h3"
            sx={{
              fontSize: { xs: 10, md: 10.5 }, fontWeight: 700, letterSpacing: "0.06em",
              textTransform: "uppercase", color: G.textSec, fontFamily: G.fontDisplay,
              // Always in full — an ellipsised title is a number with no subject.
              lineHeight: 1.35, m: 0,
            }}
          >
            {label}
          </Typography>
          {hint && (
            <Tooltip title={hint} arrow enterTouchDelay={0}>
              <InfoIcon
                tabIndex={0}
                aria-label={hint}
                sx={{ fontSize: 12, color: G.textMut, flexShrink: 0, cursor: "help" }}
              />
            </Tooltip>
          )}
          {/* The Dashboard's LIVE pill: the word, not a colour. */}
          {live && <LiveTag />}
        </Box>

        {iconNode && (
          // The Dashboard's tinted chip. Colour here identifies the figure; it
          // never judges it.
          <Box
            aria-hidden
            sx={{
              display: "grid", placeItems: "center", flexShrink: 0,
              width: { xs: 26, md: 28 }, height: { xs: 26, md: 28 }, borderRadius: "9px",
              color: accentColour, bgcolor: alpha(accentColour, 0.12), border: `1px solid ${alpha(accentColour, 0.15)}`,
              "& svg": { fontSize: 16 },
            }}
          >
            {iconNode}
          </Box>
        )}
      </Box>

      <Typography
        aria-busy={loading || undefined}
        sx={{
          // dashValueSx: the display face, bold, tight tracking, tabular figures.
          fontFamily: G.fontDisplay, fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1,
          fontVariantNumeric: "tabular-nums",
          fontSize: { xs: "clamp(22px, 6.6vw, 26px)", sm: 22, md: 25 },
          color: G.textPri,
          overflowWrap: "anywhere",
          ...(loading && {
            color: "transparent",
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            borderRadius: 1, width: "72%", minHeight: 25,
          }),
        }}
      >
        {loading ? "—" : value}
      </Typography>

      {/* Caption, pinned to the foot so a row of cards lines up. */}
      <Box sx={{ mt: "auto", pt: { xs: 0.75, md: 1 }, minHeight: 20 }}>
        {loading ? null : resolvedChange ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
            <Box
              component="span"
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.25,
                fontSize: 11.5, fontWeight: 700, fontVariantNumeric: "tabular-nums",
                // The ONLY semantic colour on the card.
                color: resolvedChange.better ? G.green : G.red,
              }}
            >
              {changeUp ? <UpIcon sx={{ fontSize: 13 }} /> : <DownIcon sx={{ fontSize: 13 }} />}
              {changeText}
            </Box>
            {(resolvedChange.note || supportText) && (
              <Typography component="span" sx={{ fontSize: 11, fontWeight: 500, color: G.textSec, lineHeight: 1.35 }}>
                {resolvedChange.note || supportText}
              </Typography>
            )}
          </Box>
        ) : supportText ? (
          <Typography sx={{ fontSize: 11, fontWeight: 500, color: G.textSec, lineHeight: 1.35, letterSpacing: "0.005em" }}>
            {supportText}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
